import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Botao,
  Breadcrumb,
  Cabecalho,
  Cores,
  ErroLayoutContainer,
  Filtro,
  IconeCheckPequeno,
  IconeRevelarSenha,
  InputUnform,
  SelectUnform,
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta,
  Tooltip
} from 'src/componentes'
import { PesquisaTicketDTO } from 'src/dto'
import { formatadores } from 'src/paginas/aluno/painel-financeiro/pagina-financeiro/formatadores'
import {
  ModalFinalizarTicket,
  ModalFinalizarTicketRef,
  FinalizarTicket
} from 'src/paginas/ipgs/filas-trabalho/componentes'
import { RotasRequerimentos } from 'src/rotas'
import { Api } from 'src/servicos'
import {
  CategoriaTicket,
  SituacaoEmailSecretaria,
  SituacaoRequerimento,
  StatusTicket
} from 'src/tipos'

import { rotuloEmailSecretaria } from '../email-secretaria'

import {
  Acoes,
  Campo,
  Container,
  Dados,
  Form,
  IconeAcao,
  Intro,
  Nota,
  Pagina,
  Pill,
  TabelaBox
} from './style'

type FiltroStatus = 'aberto' | 'finalizado' | 'cancelado' | 'todos'
type FiltroPagamento = 'todos' | 'pago' | 'aguardando'
type FiltroEmail = 'todos' | 'enviado' | 'falhou' | 'naoEnviado'

interface DadosPesquisa {
  nomeAluno?: string
  numeroProtocolo?: string
  status?: FiltroStatus
  pagamento?: FiltroPagamento
  email?: FiltroEmail
}

const TOOLTIP_DETALHE_ID = 'tooltip-ver-requerimento-certificado'
const TOOLTIP_FINALIZAR_ID = 'tooltip-finalizar-requerimento-certificado'

const PaginaFilaCertificadoImpressoConteudo: React.FC = () => {
  const history = useHistory()
  const tabelaRef = useRef<TabelaRef>()
  const modalFinalizarTicket = useRef<ModalFinalizarTicketRef>(null)
  const [dadosPesquisa, definirDadosPesquisa] = useState<DadosPesquisa>(null)
  const [paginas, definirPaginas] = useState<number>(null)

  const statusDoFiltro = (): StatusTicket[] => {
    const status = dadosPesquisa?.status ?? 'aberto'

    if (status === 'aberto') {
      return [StatusTicket.Aberto, StatusTicket.Pendente]
    }

    if (status === 'finalizado') {
      return [StatusTicket.Finalizado]
    }

    return undefined
  }

  const situacoesRequerimentoDoFiltro = ():
    | SituacaoRequerimento[]
    | undefined => {
    if (dadosPesquisa?.status === 'cancelado') {
      return [SituacaoRequerimento.Cancelado]
    }

    return undefined
  }

  const excluirCanceladosDoFiltro = (): boolean | undefined =>
    dadosPesquisa?.status === 'finalizado' ? true : undefined

  const pagamentoDoFiltro = (): boolean | undefined => {
    if (dadosPesquisa?.pagamento === 'pago') return true
    if (dadosPesquisa?.pagamento === 'aguardando') return false
    return undefined
  }

  const emailDoFiltro = (): SituacaoEmailSecretaria[] | undefined => {
    if (dadosPesquisa?.email === 'enviado') {
      return [SituacaoEmailSecretaria.Enviado]
    }
    if (dadosPesquisa?.email === 'falhou') {
      return [SituacaoEmailSecretaria.Falhou]
    }
    if (dadosPesquisa?.email === 'naoEnviado') {
      return [SituacaoEmailSecretaria.NaoEnviado]
    }
    return undefined
  }

  const textoDoFiltro = (valor?: string): string | undefined =>
    valor?.trim() || undefined

  const acaoPesquisa = (dados: DadosPesquisa) => {
    if (dados !== null && paginas !== 1) {
      definirPaginas(1)
    }

    definirDadosPesquisa(dados)
  }

  const obterDados = async (
    numeroPaginas: number,
    quantidadePorPagina: number
  ): Promise<TabelaResposta | null> => {
    if (paginas !== null) {
      numeroPaginas = paginas
    }

    const dados = await Api.RequisitarListaTickets(
      textoDoFiltro(dadosPesquisa?.nomeAluno),
      undefined,
      undefined,
      [CategoriaTicket.CertificadoImpresso],
      statusDoFiltro(),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      numeroPaginas,
      quantidadePorPagina,
      undefined,
      undefined,
      pagamentoDoFiltro(),
      emailDoFiltro(),
      textoDoFiltro(dadosPesquisa?.numeroProtocolo),
      situacoesRequerimentoDoFiltro(),
      excluirCanceladosDoFiltro()
    )

    if (!dados) return null

    definirPaginas(null)

    return {
      Dados: dados.registros,
      Paginacao: {
        Pagina:
          dados.opcoesPaginacao.totalPaginas < dados.opcoesPaginacao.pagina
            ? 1
            : dados.opcoesPaginacao.pagina,
        TotalRegistros: dados.opcoesPaginacao.totalRegistros
      }
    }
  }

  const abrirDetalhe = (requerimentoId: string) => {
    if (!requerimentoId) return
    history.push(`${RotasRequerimentos.CertificadoImpresso}/${requerimentoId}`)
  }

  const finalizarTicket = async (dados: FinalizarTicket) => {
    try {
      await Api.FinalizarTicket(dados.idTicket, dados.observacao)
      toast('Solicitação finalizada. Endereço mantido no histórico', {
        type: 'success'
      })
      tabelaRef.current?.CarregarDados()
    } catch {
      toast('Erro ao finalizar a solicitação.', { type: 'error' })
    }
  }

  const cidadeUf = (ticket: PesquisaTicketDTO) => {
    if (!ticket.cidade && !ticket.uf) return '-'
    if (ticket.cidade && ticket.uf) return `${ticket.cidade}/${ticket.uf}`
    return ticket.cidade || ticket.uf
  }

  const pillPagamento = (ticket: PesquisaTicketDTO) =>
    ticket.pagamentoConfirmado ? (
      <Pill variacao="ok">Pago</Pill>
    ) : (
      <Pill variacao="warn">Aguardando</Pill>
    )

  const pillEmail = (ticket: PesquisaTicketDTO) => {
    const rotulo = rotuloEmailSecretaria(
      ticket.situacaoEmailSecretaria,
      ticket.pagamentoConfirmado
    )
    return <Pill variacao={rotulo.variacao}>{rotulo.texto}</Pill>
  }

  const acoes = (ticket: PesquisaTicketDTO) => {
    const finalizado =
      ticket.status === StatusTicket.Finalizado ||
      ticket.situacaoRequerimento === SituacaoRequerimento.Cancelado

    return (
      <Acoes>
        <IconeAcao
          type="button"
          onClick={() => abrirDetalhe(ticket.requerimentoId)}
          disabled={!ticket.requerimentoId}
          data-tip="Ver detalhes da solicitação"
          data-for={TOOLTIP_DETALHE_ID}
        >
          {IconeRevelarSenha}
          <Tooltip
            id={TOOLTIP_DETALHE_ID}
            textColor={Cores.PRETO}
            backgroundColor={Cores.CINZA_1_CLARO}
            place="left"
          />
        </IconeAcao>
        {!finalizado && (
          <IconeAcao
            type="button"
            onClick={() => modalFinalizarTicket.current?.abrir(ticket.id)}
            disabled={!ticket.id}
            data-tip="Finalizar solicitação"
            data-for={TOOLTIP_FINALIZAR_ID}
          >
            {IconeCheckPequeno}
            <Tooltip
              id={TOOLTIP_FINALIZAR_ID}
              textColor={Cores.PRETO}
              backgroundColor={Cores.CINZA_1_CLARO}
              place="left"
            />
          </IconeAcao>
        )}
      </Acoes>
    )
  }

  useEffect(() => {
    if (dadosPesquisa === null) return
    tabelaRef.current?.CarregarDados()
  }, [dadosPesquisa])

  return (
    <Pagina data-layout="largura-total">
      <Cabecalho>
        <Breadcrumb
          titulo="Solicitações de Certificado Impresso"
          atalhos={[
            { texto: 'Requerimentos' },
            { texto: 'Certificado Impresso' }
          ]}
        />
      </Cabecalho>
      <Intro>
        <p>
          Toda solicitação paga já chegou em secretaria.pos@ipgs.com.br com o
          endereço. Pode finalizar sem perder os dados. Solicitações ainda sem
          pagamento aguardam e não geram e-mail.
        </p>
      </Intro>
      <Filtro>
        <Form
          dadosIniciais={{
            status: 'aberto',
            pagamento: 'todos',
            email: 'todos'
          }}
          acaoSucesso={acaoPesquisa}
        >
          <Container>
            <Campo mr>
              <InputUnform
                type="text"
                name="numeroProtocolo"
                label="Número de protocolo"
                maxLength={20}
              />
            </Campo>
            <Campo>
              <InputUnform
                type="text"
                name="nomeAluno"
                label="Nome"
                maxLength={200}
              />
            </Campo>
          </Container>
          <Container>
            <Campo mr>
              <SelectUnform
                id="slct_status"
                name="status"
                label="Status"
                iconeFechar={false}
                opcoes={[
                  { id: 'aberto', texto: 'Abertas' },
                  { id: 'finalizado', texto: 'Finalizadas' },
                  { id: 'cancelado', texto: 'Canceladas' },
                  { id: 'todos', texto: 'Todas' }
                ]}
              />
            </Campo>
            <Campo mr>
              <SelectUnform
                id="slct_pagamento"
                name="pagamento"
                label="Pagamento"
                iconeFechar={false}
                opcoes={[
                  { id: 'todos', texto: 'Todos' },
                  { id: 'pago', texto: 'Pago' },
                  { id: 'aguardando', texto: 'Aguardando' }
                ]}
              />
            </Campo>
            <Campo>
              <SelectUnform
                id="slct_email"
                name="email"
                label="E-mail à Secretaria"
                iconeFechar={false}
                opcoes={[
                  { id: 'todos', texto: 'Todos' },
                  { id: 'enviado', texto: 'Enviado' },
                  { id: 'falhou', texto: 'Falhou' },
                  { id: 'naoEnviado', texto: 'Não enviado' }
                ]}
              />
            </Campo>
          </Container>
          <Botao
            type="submit"
            id="btn-aplicar-filtro"
            texto="Aplicar filtro"
            tema="Link"
          />
        </Form>
      </Filtro>
      <Dados>
        <TabelaBox>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'numeroProtocolo',
              Renderizar: (linha: TabelaDados) => (
                <>
                  {((linha as unknown) as PesquisaTicketDTO).numeroProtocolo ||
                    '-'}
                </>
              ),
              StyleColuna: { minWidth: 110 },
              RenderizarCabecalho: () => <>Protocolo</>
            },
            {
              Chave: 'nomeAluno',
              Renderizar: (linha: TabelaDados) => (
                <>{linha.usuario?.nomeApresentacao || linha.usuario?.nome}</>
              ),
              StyleColuna: { minWidth: 180 },
              RenderizarCabecalho: () => <>Nome</>
            },
            {
              Chave: 'cidade',
              Renderizar: (linha: TabelaDados) => (
                <>{cidadeUf((linha as unknown) as PesquisaTicketDTO)}</>
              ),
              StyleColuna: { minWidth: 120 },
              RenderizarCabecalho: () => <>Cidade/UF</>
            },
            {
              Chave: 'dataCriacao',
              Renderizar: (linha: TabelaDados) => (
                <span>
                  {formatadores.dataHoraLocaleString(linha.dataCriacao)}
                </span>
              ),
              StyleColuna: { minWidth: 140 },
              RenderizarCabecalho: () => <>Aberta pelo aluno</>
            },
            {
              Chave: 'pagamentoConfirmado',
              Renderizar: (linha: TabelaDados) =>
                pillPagamento((linha as unknown) as PesquisaTicketDTO),
              StyleColuna: { minWidth: 110 },
              RenderizarCabecalho: () => <>Pagamento</>
            },
            {
              Chave: 'situacaoEmailSecretaria',
              Renderizar: (linha: TabelaDados) =>
                pillEmail((linha as unknown) as PesquisaTicketDTO),
              StyleColuna: { minWidth: 160 },
              RenderizarCabecalho: () => <>E-mail à Secretaria</>
            },
            {
              Chave: 'status',
              Renderizar: (linha: TabelaDados) => {
                const ticket = (linha as unknown) as PesquisaTicketDTO
                if (
                  ticket.situacaoRequerimento === SituacaoRequerimento.Cancelado
                ) {
                  return <Pill variacao="erro">Cancelado</Pill>
                }

                return ticket.status === StatusTicket.Finalizado ? (
                  <Pill variacao="ok">Finalizado</Pill>
                ) : (
                  <Pill variacao="warn">Aberto</Pill>
                )
              },
              StyleColuna: { minWidth: 90 },
              RenderizarCabecalho: () => <>Status</>
            },
            {
              Chave: 'acoes',
              Renderizar: (linha: TabelaDados) =>
                acoes((linha as unknown) as PesquisaTicketDTO),
              StyleColuna: { minWidth: 90 },
              RenderizarCabecalho: () => <>Ações</>
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
          TextoSemResultados="Nenhuma solicitação neste filtro."
        />
      </TabelaBox>
      </Dados>
      <Nota>
        Finalizadas saem da fila de abertas e ficam em Finalizadas, com o
        endereço preservado.
      </Nota>
      <ModalFinalizarTicket
        id="modal-finalizar-ticket-fila-certificado"
        ref={modalFinalizarTicket}
        backdrop
        acaoPrimaria={finalizarTicket}
      />
    </Pagina>
  )
}

export const PaginaFilaCertificadoImpresso: React.FC = () => (
  <ErroLayoutContainer.Provider>
    <PaginaFilaCertificadoImpressoConteudo />
  </ErroLayoutContainer.Provider>
)

export default PaginaFilaCertificadoImpresso
